import React, { useState } from 'react';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import InquiryForm from './InquiryForm';
import { useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Loan Products', href: '/#loan-programs' },
    { name: 'Marketplace', href: 'https://getzorba.com/partner/close-now-lending', external: true },
    { name: 'Agents', href: '/agents' },
    { 
      name: 'Resources', 
      href: '#',
      dropdown: [
        { name: 'DSCR Calculator', href: '/dscr-calculator' },
        { name: 'Fix & Flip Deal Calculator', href: '/deal-calculator' },
        { name: 'Learning Center', href: '/blog' },
        { name: 'FAQ', href: '/faq' },
        { name: 'CE Courses', href: 'https://deallabce.com/public', external: true }
      ]
    },
    { 
      name: 'About Us', 
      href: '#',
      dropdown: [
        { name: 'About Us', href: '/about' },
        { name: 'Team', href: '/team' },
        { name: 'Join Our Team', href: '/join' },
        { name: 'Contact', href: '/contact' }
      ]
    }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    
    if (href === '/' || href.startsWith('/#')) {
      // If we're on homepage, scroll to section
      if (location.pathname === '/') {
        if (href.startsWith('/#')) {
          const sectionId = href.substring(2);
          const element = document.querySelector(`#${sectionId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }
      } else {
        // Navigate to homepage first, then scroll
        navigate(href);
      }
    } else {
      // Regular page navigation
      navigate(href);
    }
    
    setIsMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleGetStarted = () => {
    setIsFormOpen(true);
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className="bg-white shadow-sm fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_130b504f-d356-4ffc-9f88-b9ceecab1da1/artifacts/djtji90d_logo_large%20-%20Edited.png" 
                alt="Close Now Lending - Nationwide Hard Money, Fix and Flip, DSCR, and Commercial Real Estate Loans" 
                className="h-20 w-auto -my-4 scale-[2.2] origin-left"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navigation.map((item) => (
                item.dropdown ? (
                  <div 
                    key={item.name} 
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(item.name)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <button
                      className="flex items-center text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 py-2"
                    >
                      {item.name}
                      <ChevronDown className="h-4 w-4 ml-1" />
                    </button>
                    {openDropdown === item.name && (
                      <div className="absolute top-full left-0 pt-1 w-56 z-50">
                        <div className="bg-white rounded-lg shadow-lg py-2 border border-gray-100">
                          {item.dropdown.map((subItem) => (
                            <a
                              key={subItem.name}
                              href={subItem.href}
                              {...(subItem.external ? { target: '_blank', rel: 'noopener noreferrer' } : { onClick: (e) => scrollToSection(e, subItem.href) })}
                              className="block px-4 py-2.5 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                            >
                              {subItem.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <a
                    key={item.name}
                    href={item.href}
                    {...(item.external ? { target: '_blank', rel: 'noopener noreferrer' } : { onClick: (e) => scrollToSection(e, item.href) })}
                    className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                  >
                    {item.name}
                  </a>
                )
              ))}
            </nav>

            {/* Desktop Contact & CTA */}
            <div className="hidden lg:flex items-center space-x-4">
              <a href="tel:+19034762937" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                <Phone className="h-4 w-4 mr-2" />
                <span className="font-medium">903-476-2937</span>
              </a>
              <Button 
                onClick={handleGetStarted}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200 animate-in slide-in-from-top-2">
              <nav className="flex flex-col space-y-4">
                {navigation.map((item) => (
                  item.dropdown ? (
                    <div key={item.name} className="px-4">
                      <div className="font-medium text-gray-900 mb-2">{item.name}</div>
                      <div className="pl-4 space-y-2">
                        {item.dropdown.map((subItem) => (
                          <a
                            key={subItem.name}
                            href={subItem.href}
                            {...(subItem.external ? { target: '_blank', rel: 'noopener noreferrer' } : { onClick: (e) => scrollToSection(e, subItem.href) })}
                            className="block text-gray-700 hover:text-blue-600 py-2 transition-colors"
                          >
                            {subItem.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <a
                      key={item.name}
                      href={item.href}
                      {...(item.external ? { target: '_blank', rel: 'noopener noreferrer' } : { onClick: (e) => scrollToSection(e, item.href) })}
                      className="text-gray-700 hover:text-blue-600 font-medium transition-colors px-4 py-2 rounded-md hover:bg-gray-50"
                    >
                      {item.name}
                    </a>
                  )
                ))}
                <div className="px-4 pt-4 border-t border-gray-200 space-y-3">
                  <a href="tel:+19034762937" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                    <Phone className="h-4 w-4 mr-2" />
                    <span className="font-medium">903-476-2937</span>
                  </a>
                  <Button 
                    onClick={handleGetStarted}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Get Started
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      <InquiryForm 
        isOpen={isFormOpen} 
        onClose={() => setIsFormOpen(false)}
        loanType=""
      />
    </>
  );
};

export default Header;